import { createSelector } from '@reduxjs/toolkit';
import { repairKey } from './slice';

const EMPTY_ROWS = [];
const EMPTY_LIST = [];

const selectSlice = (state) => state.CardStock || {};

export const selectDraft = (state, siteID) => selectSlice(state).drafts?.[siteID] || null;
export const selectDraftRows = (state, siteID) => selectDraft(state, siteID)?.rows || EMPTY_ROWS;

/**
 * What a submit would send, and why the rest is held back (§4.1 `POST stock/:siteID/enrol`, §5.1.5).
 * Reads the classify row's `status` (row 6 `classifyCards`).
 * Sent: `new` and `in_stock` cards, and `needs_confirmation` cards staff confirmed — with the holder they were shown,
 * so the server moves a card only from the holder staff agreed to (null for an unassigned card).
 * Held back: refused cards, unconfirmed cards, cards the caller may not move (`canConfirm: false`), and cards not yet
 * checked.
 */
export const selectEnrolPlan = createSelector([selectDraftRows], (rows) => {
	const cards = [];
	const held = { refused: 0, unconfirmed: 0, unchecked: 0, notAllowed: 0 };
	rows.forEach((r) => {
		if (!r.csn) {
			held.refused += 1;
			return;
		}
		const c = r.classification;
		if (!c) {
			held.unchecked += 1;
			return;
		}
		const base = r.name ? { csn: r.csn, name: r.name } : { csn: r.csn };
		if (c.status === 'new' || c.status === 'in_stock') cards.push(base);
		else if (c.status === 'needs_confirmation') {
			// canConfirm false: the holder is outside the caller's sites, and the server refuses the WHOLE list if
			// such a card is sent confirmed. Never sent, whatever `confirmed` says.
			if (c.canConfirm === false) held.notAllowed += 1;
			else if (r.confirmed)
				cards.push({
					...base,
					confirm: {
						credentialID: Number(c.credentialID),
						expectedHolderPersonID: c.holderPersonID == null ? null : Number(c.holderPersonID)
					}
				});
			else held.unconfirmed += 1;
		} else held.refused += 1;
	});
	return { cards, held };
});

/**
 * The cards as one canonical string, sorted by CSN, so the same list in another order is the same submit. It decides
 * whether a resend reuses the draft's `enrolKey` (same string) or needs a new one.
 */
export const canonicalEnrolCards = (cards) =>
	JSON.stringify(
		[...cards]
			.sort((a, b) => (a.csn < b.csn ? -1 : a.csn > b.csn ? 1 : 0))
			.map((c) => [c.csn, c.name ?? null, c.confirm?.credentialID ?? null, c.confirm?.expectedHolderPersonID ?? null])
	);

export const selectStockCards = (state, siteID) => selectSlice(state).cardsBySite?.[siteID] || null;
export const selectStockCardsLoading = (state, siteID) => Boolean(selectSlice(state).cardsLoading?.[siteID]);
export const selectStockCardsError = (state, siteID) => selectSlice(state).cardsError?.[siteID] || null;

/**
 * Card Stock then Card Store cards, "Access to remove" first (§5.2). Otherwise the server's own order is kept: it
 * already sorts by state, and the Dashboard does not rank delivery states of its own.
 */
export const selectStockCardList = createSelector([selectStockCards], (data) => {
	if (!data) return EMPTY_LIST;
	const all = [...(data.stock || []), ...(data.store || [])];
	const toRemove = all.filter((c) => c.delivery?.state === 'access_to_remove');
	return [...toRemove, ...all.filter((c) => c.delivery?.state !== 'access_to_remove')];
});

export const selectRepairing = (state, siteID, credentialID) =>
	Boolean(selectSlice(state).repairing?.[repairKey(siteID, credentialID)]);
export const selectRepairResult = (state, siteID, credentialID) =>
	selectSlice(state).repairResults?.[repairKey(siteID, credentialID)] || null;
